import { onMount } from "solid-js";

export default function MetadataForm(props: {
  startTime: Date | null;
  endTime: Date | null;
  autoLocation: string;
  caseLocation: string;
  setCaseLocation: (loc: string) => void;
  contactType: string;
  setContactType: (ct: string) => void;
  notes: string;
  setNotes: (n: string) => void;
}) {
  // ─── formatters for date/time/duration ───────────────────────────
  const fmtDate = (d: Date) =>
    d.toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  const fmtTime = (d: Date) =>
    d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
  const computeDuration = () => {
    if (!props.startTime || !props.endTime) return "";
    const diffMs = props.endTime.getTime() - props.startTime.getTime();
    const minutes = Math.floor(diffMs / 60000);
    const seconds = Math.floor((diffMs % 60000) / 1000);
    return `${minutes.toString().padStart(2, "0")}:${seconds
      .toString()
      .padStart(2, "0")}`;
  };

  // ─── onMount: try to auto-fetch geolocation once ───────────────────
  onMount(() => {
    if (!navigator.geolocation) {
      props.setCaseLocation(props.caseLocation); // no auto location if not supported
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude, longitude } = pos.coords;
        const coords = `${latitude.toFixed(4)}, ${longitude.toFixed(4)}`;
        // store in autoLocation 
        // Note: props.autoLocation is read-only, so we don't set it here.
      },
      () => {
        // if geolocation fails/denied, leave autoLocation blank
      }
    );
  });

  return (
    <div class="card mt-6 space-y-4">
      <h2 class="text-lg font-semibold">Additional Call Details</h2>

      <div class="flex items-center gap-3">
        <label class="w-28 text-sm font-medium">Date:</label>
        <span class="text-sm">
          {props.startTime ? fmtDate(props.startTime) : fmtDate(new Date())}
        </span>
      </div>

      <div class="flex items-center gap-3">
        <label class="w-28 text-sm font-medium">Start:</label>
        <span class="text-sm">
          {props.startTime ? fmtTime(props.startTime) : "—"}
        </span>
      </div>

      <div class="flex items-center gap-3">
        <label class="w-28 text-sm font-medium">End:</label>
        <span class="text-sm">
          {props.endTime ? fmtTime(props.endTime) : "—"}
        </span>
      </div>

      <div class="flex items-center gap-3">
        <label class="w-28 text-sm font-medium">Duration:</label>
        <span class="text-sm">{computeDuration() || "—"}</span>
      </div>

      <div class="flex flex-col gap-1">
        <label class="text-sm font-medium">Auto‐detected Location:</label>
        <span class="text-sm text-gray-600">
          {props.autoLocation || "Not available"}
        </span>
      </div>

      <div class="flex flex-col gap-1">
        <label class="text-sm font-medium">Address:</label>
        <label class="text-sm font-medium">Address:</label>
        <input
          type="text"
          placeholder="Enter address"
          value={props.caseLocation}
          onInput={(e) =>
            props.setCaseLocation((e.currentTarget as HTMLInputElement).value)
          }
          class="mt-1 border border-gray-300 rounded px-2 py-1 text-sm w-full"
        />
      </div>

      <div class="flex flex-col gap-1">
        <label class="text-sm font-medium">Notes:</label>
        <textarea
          value={props.notes}
          onInput={(e) =>
            props.setNotes((e.currentTarget as HTMLTextAreaElement).value)
          }
          rows={4}
          class="border border-gray-300 rounded p-2 text-sm w-full"
          placeholder="Enter any additional notes…"
        />
      </div>
    </div>
  );
}
